import { useState, useMemo } from "react";
import { Calculator, Droplets, Info } from "lucide-react";
import { Input } from "./ui/input";

const GAL_PER_CUFT = 7.48;

function fmt(n, decimals = 0) {
  if (!isFinite(n) || isNaN(n)) return "—";
  return n.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

// small numeric field
function Field({ label, value, onChange, suffix, min = 0, step = "any" }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-[#4a4a4a] mb-2">{label}</span>
      <div className="relative">
        <Input
          type="number"
          min={min}
          step={step}
          value={value}
          onChange={(e) => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          className="bg-white pr-16"
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#9aa2ad] font-medium pointer-events-none">
            {suffix}
          </span>
        )}
      </div>
    </label>
  );
}

function ResultRow({ label, value, highlight = false }) {
  return (
    <div
      className={`flex items-center justify-between py-3 px-4 rounded-lg ${
        highlight ? "bg-[#d81b76]/10 border border-[#d81b76]/30" : "border-b border-gray-100"
      }`}
    >
      <span className={`text-sm ${highlight ? "font-semibold text-[#1a2540]" : "text-[#6a6a6a]"}`}>
        {label}
      </span>
      <span
        className={`font-heading ${
          highlight ? "text-lg text-[#d81b76]" : "text-sm text-[#1a2540]"
        }`}
      >
        {value}
      </span>
    </div>
  );
}

export default function PinkwaterCalculator() {
  const [tab, setTab] = useState("tank");

  // Part 1 — Tank sludge estimate
  const [diameter, setDiameter] = useState(100);
  const [depthIn, setDepthIn] = useState(3);
  const [hcPct, setHcPct] = useState(50);
  const [ratio, setRatio] = useState(20);
  const [workingPct, setWorkingPct] = useState(4);

  const part1 = useMemo(() => {
    const d = Number(diameter) || 0;
    const area = Math.PI * Math.pow(d / 2, 2);
    const depthFt = (Number(depthIn) || 0) / 12;
    const sludgeVol = area * depthFt;
    const hcVol = sludgeVol * ((Number(hcPct) || 0) / 100);
    const hcGal = hcVol * GAL_PER_CUFT;
    const concentrate = (Number(ratio) || 0) > 0 ? hcGal / Number(ratio) : 0;
    const solution = (Number(workingPct) || 0) > 0 ? concentrate / (Number(workingPct) / 100) : 0;
    return { area, depthFt, sludgeVol, hcVol, hcGal, concentrate, solution };
  }, [diameter, depthIn, hcPct, ratio, workingPct]);

  // Part 2 — Frac tank wash
  const [waterGal, setWaterGal] = useState(10000);
  const [afterPct, setAfterPct] = useState(0.5);
  const [oilGal, setOilGal] = useState(2000);

  const part2 = useMemo(() => {
    const afterSkim = (Number(waterGal) || 0) * ((Number(afterPct) || 0) / 100);
    const beforeSkim = (Number(oilGal) || 0) * 0.05;
    return { afterSkim, beforeSkim, recommended: Math.max(afterSkim, beforeSkim) };
  }, [waterGal, afterPct, oilGal]);

  return (
    <section className="py-20 bg-white">
      <div className="mx-auto max-w-[1000px] px-6">
        <div className="text-center mb-10">
          <p className="msv-eyebrow mb-4">Plan Your Order</p>
          <h2 className="font-heading text-3xl md:text-4xl uppercase tracking-[0.08em] text-[#1a2540]">
            Pinkwater® Volume Estimator
          </h2>
          <p className="text-[#6a6a6a] leading-relaxed mt-5 max-w-2xl mx-auto">
            Estimate how much Pinkwater® concentrate and working solution your job requires. Enter
            your values below for an instant guideline estimate.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex bg-[#f6f8fa] rounded-xl border border-gray-200 p-1">
            {[
              { id: "tank", label: "Tank Sludge Estimate" },
              { id: "frac", label: "Frac Tank Wash" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`font-heading uppercase tracking-[0.08em] text-xs sm:text-sm px-5 py-2.5 rounded-lg transition-all duration-200 ${
                  tab === t.id ? "bg-[#d81b76] text-white shadow-sm" : "text-[#6a6a6a] hover:text-[#1a2540]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="msv-card p-6 md:p-10">
          {tab === "tank" ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Inputs */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Calculator size={20} className="text-[#d81b76]" />
                  <h3 className="font-heading text-lg uppercase tracking-[0.05em] text-[#1a2540]">
                    Tank Details
                  </h3>
                </div>
                <div className="space-y-5">
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Tank Diameter" value={diameter} onChange={setDiameter} suffix="ft" />
                    <Field label="Sludge Depth" value={depthIn} onChange={setDepthIn} suffix="in" />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Hydrocarbon Content" value={hcPct} onChange={setHcPct} suffix="%" />
                    <Field label="Working Solution" value={workingPct} onChange={setWorkingPct} suffix="%" />
                  </div>
                  <Field
                    label="Dilution Ratio (hydrocarbons : concentrate)"
                    value={ratio}
                    onChange={setRatio}
                    suffix=": 1"
                  />
                </div>
              </div>

              {/* Results */}
              <div className="bg-[#f6f8fa] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Droplets size={20} className="text-[#d81b76]" />
                  <h3 className="font-heading text-lg uppercase tracking-[0.05em] text-[#1a2540]">
                    Estimated Requirement
                  </h3>
                </div>
                <div className="space-y-1">
                  <ResultRow label="Tank area" value={`${fmt(part1.area)} sq ft`} />
                  <ResultRow label="Sludge volume" value={`${fmt(part1.sludgeVol)} cu ft`} />
                  <ResultRow label="Hydrocarbon volume" value={`${fmt(part1.hcVol)} cu ft`} />
                  <ResultRow label="Hydrocarbons" value={`${fmt(part1.hcGal)} gal`} />
                  <ResultRow
                    label="Pinkwater® concentrate"
                    value={`${fmt(part1.concentrate)} gal`}
                    highlight
                  />
                  <ResultRow
                    label={`Working solution (${workingPct || 0}%)`}
                    value={`${fmt(part1.solution)} gal`}
                    highlight
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Inputs */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <Calculator size={20} className="text-[#d81b76]" />
                  <h3 className="font-heading text-lg uppercase tracking-[0.05em] text-[#1a2540]">
                    Frac Tank Details
                  </h3>
                </div>
                <div className="space-y-6">
                  <div>
                    <p className="font-heading uppercase tracking-[0.08em] text-xs text-[#1a2540] mb-3">
                      After Skimming (based on water)
                    </p>
                    <div className="grid grid-cols-2 gap-4 items-end">
                      <Field label="Residual Water" value={waterGal} onChange={setWaterGal} suffix="gal" />
                      <div>
                        <span className="block text-sm font-medium text-[#4a4a4a] mb-2">Target Solution</span>
                        <div className="flex bg-[#f6f8fa] rounded-lg border border-gray-200 p-1">
                          {[0.5, 1].map((p) => (
                            <button
                              key={p}
                              onClick={() => setAfterPct(p)}
                              className={`flex-1 text-sm font-heading py-2 rounded-md transition-colors ${
                                afterPct === p ? "bg-[#d81b76] text-white" : "text-[#6a6a6a]"
                              }`}
                            >
                              {p}%
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <p className="font-heading uppercase tracking-[0.08em] text-xs text-[#1a2540] mb-3">
                      Before Skimming (based on oil)
                    </p>
                    <Field label="Estimated Free Oil" value={oilGal} onChange={setOilGal} suffix="gal" />
                    <p className="text-xs text-[#9aa2ad] mt-2">Uses at least 5% of estimated hydrocarbons.</p>
                  </div>
                </div>
              </div>

              {/* Results */}
              <div className="bg-[#f6f8fa] rounded-xl p-6">
                <div className="flex items-center gap-2 mb-5">
                  <Droplets size={20} className="text-[#d81b76]" />
                  <h3 className="font-heading text-lg uppercase tracking-[0.05em] text-[#1a2540]">
                    Pinkwater® Needed
                  </h3>
                </div>
                <div className="space-y-1">
                  <ResultRow label={`After skimming (${afterPct}% of water)`} value={`${fmt(part2.afterSkim)} gal`} />
                  <ResultRow label="Before skimming (5% of oil)" value={`${fmt(part2.beforeSkim)} gal`} />
                  <ResultRow label="Recommended (larger value)" value={`${fmt(part2.recommended)} gal`} highlight />
                </div>
                <div className="flex items-start gap-2 mt-5 text-xs text-[#6a6a6a]">
                  <Info size={15} className="text-[#d81b76] mt-0.5 shrink-0" />
                  <span>
                    Always use the larger of the two calculations. Depending on the specific
                    hydrocarbons, a 1% solution may be required after skimming.
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs text-[#9aa2ad] text-center mt-5 max-w-2xl mx-auto">
          Estimates are provided as general guidelines only. For precise recommendations, please
          contact Market Street Ventures.
        </p>
      </div>
    </section>
  );
}
